import React from "react";

export default function TableCard({
    table,
    onEdit,
    onDelete,
    onStatusChange,
    onOpen
}) {
  const getStatusClass = (status) => {
    switch (status) {
      case "FREE":
        return "so-status-free";

      case "RESERVED":
        return "so-status-reserved";

      case "OCCUPIED":
        return "so-status-occupied";

      case "CLEANING":
        return "so-status-cleaning";

      case "OUT_OF_SERVICE":
        return "so-status-out";

      default:
        return "so-status-out";
    }
  };

  return (
    <div
      onClick={() => onOpen(table)}
      className="so-table-card-root cursor-pointer rounded-xl p-5"
    >
      {/* Scoped SwiftOrder theme — same tokens as the rest of the POS. */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

        .so-table-card-root {
          --ink: #1c1a17;
          --paper: #fbf9f4;
          --paper-dim: #f2ede1;
          --chili: #e4572e;
          --chili-dark: #c43f1a;
          --herb: #3f6b4f;
          --herb-dark: #2d4f39;
          --mustard: #f2a93b;
          --stone: #6b6459;
          --stone-light: #a49c8d;
          --line: #ddd4c2;
          --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
          --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          --font-mono: 'JetBrains Mono', 'Courier New', monospace;

          background: #fffdf8;
          border: 1px solid var(--line);
          font-family: var(--font-body);
          color: var(--ink);
          transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
        }

        .so-table-card-root * {
          box-sizing: border-box;
        }

        .so-table-card-root:hover {
          border-color: var(--chili);
          transform: translateY(-2px);
          box-shadow: 0 16px 28px -20px rgba(28, 26, 23, 0.4);
        }

        .so-table-card-title {
          font-family: var(--font-display);
          font-size: 1.35rem;
          letter-spacing: 0.01em;
          color: var(--ink);
        }

        .so-table-card-capacity {
          font-size: 0.85rem;
          color: var(--stone);
          margin-top: 4px;
        }

        .so-status-badge {
          padding: 5px 12px;
          border-radius: 999px;
          font-size: 0.7rem;
          font-weight: 700;
          font-family: var(--font-mono);
          letter-spacing: 0.04em;
        }

        .so-status-free {
          background: #eaf3ee;
          color: var(--herb-dark);
        }

        .so-status-reserved {
          background: #fdf3e2;
          color: #854f0b;
        }

        .so-status-occupied {
          background: #fdece6;
          color: var(--chili-dark);
        }

        .so-status-cleaning {
          background: #e6f1fb;
          color: #0c447c;
        }

        .so-status-out {
          background: var(--paper-dim);
          color: var(--stone);
        }

        .so-table-card-notes {
          font-size: 0.88rem;
          color: var(--stone);
        }

        .so-table-card-label {
          font-size: 0.78rem;
          font-weight: 700;
          font-family: var(--font-mono);
          letter-spacing: 0.03em;
          color: var(--stone);
          display: block;
          margin-bottom: 8px;
        }

        .so-table-card-select {
          width: 100%;
          border: 1px solid var(--line);
          border-radius: 8px;
          padding: 8px 12px;
          background: var(--paper);
          color: var(--ink);
          font-size: 0.88rem;
          outline: none;
          transition: border-color 0.15s ease;
        }

        .so-table-card-select:focus {
          border-color: var(--chili);
        }

        .so-table-card-edit {
          background: var(--ink);
          color: var(--paper);
          transition: background 0.15s ease;
        }

        .so-table-card-edit:hover {
          background: #302c27;
        }

        .so-table-card-delete {
          background: var(--chili);
          color: var(--paper);
          transition: background 0.15s ease;
        }

        .so-table-card-delete:hover {
          background: var(--chili-dark);
        }
      `}</style>

      {/* Header */}
      <div className="flex justify-between items-center">

        <div>

          <h2 className="so-table-card-title">
            🍽 {table.tableName}
          </h2>

          <p className="so-table-card-capacity">
            Capacity : {table.capacity}
          </p>

        </div>

        <span
          className={`so-status-badge ${getStatusClass(table.status)}`}
        >
          {table.status.replace("_", " ")}
        </span>

      </div>

      {/* Notes */}

      <div className="mt-4 min-h-[50px]">

        <p className="so-table-card-notes">
          {table.notes
            ? table.notes
            : "No Notes"}
        </p>

      </div>

      {/* Status */}

      <div className="mt-5">

        <label className="so-table-card-label">
          Change Status
        </label>

        <select
          value={table.status}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) =>
            onStatusChange(table.id, e.target.value)

          }
          className="so-table-card-select"
        >
          <option value="FREE">
            FREE
          </option>

          <option value="RESERVED">
            RESERVED
          </option>

          <option value="OCCUPIED">
            OCCUPIED
          </option>

          <option value="CLEANING">
            CLEANING
          </option>

          <option value="OUT_OF_SERVICE">
            OUT OF SERVICE
          </option>

        </select>

      </div>

      {/* Footer */}

      <div className="flex gap-3 mt-6">

      <button
        onClick={(e) => {
        e.stopPropagation();
        onEdit(table);
        }}
        className="so-table-card-edit flex-1 rounded-lg py-2"
        >
        Edit
       </button>

       <button
        onClick={(e) => {
        e.stopPropagation();
        onDelete(table.id);
       }}
      className="so-table-card-delete flex-1 rounded-lg py-2"
     >
  Delete
</button>

      </div>

    </div>
  );
}






// import React from "react";


// export default function TableCard({
//     table,
//     onEdit,
//     onDelete,
//     onStatusChange,
//     onOpen
// }) {
//   const getStatusColor = (status) => {
//     switch (status) {
//       case "FREE":
//         return "bg-green-100 text-green-700";

//       case "RESERVED":
//         return "bg-yellow-100 text-yellow-700";

//       case "OCCUPIED":
//         return "bg-red-100 text-red-700";

//       case "CLEANING":
//         return "bg-blue-100 text-blue-700";

//       case "OUT_OF_SERVICE":
//         return "bg-gray-200 text-gray-700";

//       default:
//         return "bg-gray-100 text-gray-700";
//     }
//   };

//   return (
// <div
//   onClick={() => onOpen(table)}
//   className="cursor-pointer bg-white rounded-xl shadow hover:shadow-lg transition p-5 border"
// >
//       {/* Header */}
//       <div className="flex justify-between items-center">

//         <div>

//           <h2 className="text-xl font-bold text-slate-800">
//             🍽 {table.tableName}
//           </h2>

//           <p className="text-sm text-slate-500 mt-1">
//             Capacity : {table.capacity}
//           </p>

//         </div>

//         <span
//           className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
//             table.status
//           )}`}
//         >
//           {table.status.replace("_", " ")}
//         </span>

//       </div>

//       {/* Notes */}

//       <div className="mt-4 min-h-[50px]">

//         <p className="text-sm text-slate-600">
//           {table.notes
//             ? table.notes
//             : "No Notes"}
//         </p>

//       </div>

//       {/* Status */}

//       <div className="mt-5">

//         <label className="text-sm font-medium block mb-2">
//           Change Status
//         </label>

//         <select
//   value={table.status}
//   onClick={(e) => e.stopPropagation()}
//   onChange={(e) =>
//     onStatusChange(table.id, e.target.value)
  
//           }
//           className="w-full border rounded-lg px-3 py-2"
//         >
//           <option value="FREE">
//             FREE
//           </option>

//           <option value="RESERVED">
//             RESERVED
//           </option>

//           <option value="OCCUPIED">
//             OCCUPIED
//           </option>

//           <option value="CLEANING">
//             CLEANING
//           </option>

//           <option value="OUT_OF_SERVICE">
//             OUT OF SERVICE
//           </option>

//         </select>

//       </div>

//       {/* Footer */}

//       <div className="flex gap-3 mt-6">

//       <button
//         onClick={(e) => {
//         e.stopPropagation();
//         onEdit(table);
//         }}
//         className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2"
//         >
//         Edit
//        </button>

//        <button
//         onClick={(e) => {
//         e.stopPropagation();
//         onDelete(table.id);
//        }}
//       className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-lg py-2"
//      >
//   Delete
// </button>

//       </div>

//     </div>
//   );
// }
